/* eslint-disable no-console */
import { datadogLogs } from '@datadog/browser-logs';
import Cookies from 'js-cookie';

import { isWebView as getIsWebView, isSSR } from '../helpers';
import { isRequestBrowserError } from '../queryUtils';
import { removeSensitiveData } from './utils';

export type MessageContext = {
    component: DDComponent;
    [key: string]: any;
};

const BP_ID_COOKIE_NAME = 'userAuthId';
const SESSION_EXPIRE_TIME_COOKIE_NAME = 'sessionExpireTime';

// TODO: need to find solution for Next.js - check ENV variable? - update when setting up logging for Next.js
const host = typeof window !== 'undefined' ? window.location.host : '';

const getEnvFromHost = () => {
    const envsMap: { [host: string]: string } = {
        'www.telia.lt': 'prod',
        'www-preprod.telia.lt': 'preprod',
        'www-qas.telia.lt': 'qas',
        'www-qas3.telia.lt': 'qas3',
        'www-qas4.telia.lt': 'qas4',
        'www-dev.telia.lt': 'dev',
        'release-qas.telia.lt': 'rqas',
        'localhost:9000': 'local',
        'localhost:3000': 'local',
        'localhost:3001': 'local',
    };

    return envsMap[host] || 'OTHER';
};

const isDatadogEnabled = [
    'www.telia.lt',
    'www-qas.telia.lt',
    'www-qas3.telia.lt',
    'www-qas4.telia.lt',
    'www-preprod.telia.lt',
    'www-dev.telia.lt',
    'release-qas.telia.lt',
].includes(host);

export const setupTeliaLtFrontEnd = () => {
    if (isDatadogEnabled) {
        datadogLogs.init({
            clientToken: 'pub598f329a1be06dc22714417f5390cd5d',
            site: 'datadoghq.eu',
            service: 'telia-lt-front-end',
            version: process.env.GIT_HASH,
            env: getEnvFromHost(),
            forwardErrorsToLogs: false,
            silentMultipleInit: true,
            beforeSend: (event: any) => {
                if (isRequestBrowserError(event) || isRequestBrowserError(event.error)) {
                    return false;
                }
                if (event.error) {
                    event.error = removeSensitiveData(event.error);
                }
                if (event.config) {
                    event.config = removeSensitiveData(event.config);
                }
                if (event.message) {
                    event.message = removeSensitiveData(event.message);
                }
                if (event.message?.status) {
                    event.message.httpStatus = event.message.status;
                    delete event.message.status;
                }
                event.requestId =
                    event.error?.config?.headers?.['X-Request-ID'] ||
                    event?.config?.headers?.['X-Request-ID'] ||
                    event?.message?.config?.headers?.['X-Request-ID'];

                return true;
            },
        });

        const bpId = Cookies.get(BP_ID_COOKIE_NAME);
        const sessionExpireTime = Cookies.get(SESSION_EXPIRE_TIME_COOKIE_NAME);
        const isWebView = !isSSR() && getIsWebView();

        datadogLogs.setGlobalContext({
            h2id: 'hid100009293',
            'business-unit': 'lithuania',
            'billing-code': '19392334',
            bpId,
            sessionExpireTime,
            isWebView,
        });

        return datadogLogs;
    } else if (process.env.NODE_ENV === 'test') {
        return {
            logger: {
                error: (_message: string, _messageContext: MessageContext, error?: Error) => undefined,
                log: (_message: string, _messageContext: MessageContext, error?: Error) => undefined,
                warn: (_message: string, _messageContext: MessageContext, error?: Error) => undefined,
                info: (_message: string, _messageContext: MessageContext, error?: Error) => undefined,
            },
        };
    } else {
        return {
            logger: {
                error(_message: string, _messageContext: MessageContext, error?: Error) {
                    return console.error(...arguments);
                },
                log(_message: string, _messageContext: MessageContext, error?: Error) {
                    return console.log(...arguments);
                },
                warn(_message: string, _messageContext: MessageContext, error?: Error) {
                    return console.warn(...arguments);
                },
                info(_message: string, _messageContext: MessageContext, error?: Error) {
                    return console.info(...arguments);
                },
            },
        };
    }
};

export enum DDComponent {
    Eshop = 'telia-lt-eshop-app',
    SelfService = 'telia-lt-self-service-app',
    TopUp = 'telia-lt-data-top-up-app',
    PublicWeb = 'telia-lt-public-web-app',
    Payment = 'telia-lt-payment',
    PlanApps = 'telia-lt-plan-apps',
    ContextWidget = 'telia-lt-context-widget',
    B2BSelfService = 'telia-lt-b2b-self-service-app',
    B2CSelfService = 'telia-lt-b2c-self-service-app',
    InternetCampaign = 'telia-lt-internet-campaign-app',
    ServiceFeatures = 'telia-lt-service-features-app',
    AdditionalService = 'telia-lt-additional-service-app',
    RouteCableForm = 'telia-lt-route-cable-form-app',
    TariffsAndRoaming = 'telia-lt-tariffs-and-roaming-app',
    TgCheck = 'telia-lt-tg-check-app',
    UpsellOffers = 'telia-lt-upsell-offers-app',
    DeviceSupportTool = 'telia-lt-device-support-tool-app',
    EshopCheckoutB2B = 'telia-lt-eshop-checkout-b2b-app',
    EshopCheckoutB2C = 'telia-lt-eshop-checkout-b2c-app',
    EshopCheckout = 'telia-lt-eshop-checkout',
    Navbar = 'telia-lt-navbar-app',
    PersonalNotification = 'telia-lt-personal-notification-app',
    PushOffers = 'telia-lt-push-offers-app',
    PlanChangeSelfService = 'telia-lt-plan-change-self-service',
    SimCardManagementSelfService = 'telia-lt-sim-card-management-self-service',
    DocumentsSelfService = 'telia-lt-documents-self-service',
    InvoicesSelfService = 'telia-lt-invoices-self-service',
    OverduePaymentSelfService = 'telia-lt-overdue-payment-self-service',
    B2BSelfServiceDashboard = 'telia-lt-b2b-self-service-dashboard',
    B2BSelfServiceMobInvoice = 'telia-lt-b2b-self-service-mob-invoice',
    B2CSelfServiceDashboard = 'telia-lt-b2c-self-service-dashboard',
    DebtAlignmentSelfService = 'telia-lt-debt-alignment-self-service',
    MandatesSelfService = 'telia-lt-mandates-self-service',
    ServicesSelfService = 'telia-lt-services-self-service',
    SettingsSelfService = 'telia-lt-settings-self-service',
    PersonalOffersSelfService = 'telia-lt-personal-offers-self-service',
    CoreSelfService = 'telia-lt-core-self-service',
    InstallmentsSelfService = 'telia-lt-installments-self-service',
    InternalMessagesSelfService = 'telia-lt-internal-messages-self-service',
    EshopProductCore = 'telia-lt-eshop-product-core',
    EshopUpsell = 'telia-lt-eshop-upsell',
    EshopCheckoutCore = 'telia-lt-eshop-checkout-core',
    SSO = 'telia-lt-sso-app',
    Search = 'telia-lt-search',
    HostexLogin = 'telia-lt-hostex-login',
    AddressOld = 'telia-lt-address-old',
    PaymentOld = 'telia-lt-payment-old',
    AUTH = 'telia-lt-auth',
    Messages = 'telia-lt-messages',
    CMSComponents = 'telia-lt-cms-components',
    HelpCenter = 'telia-lt-help-center',
    WifiSettings = 'telia-lt-wifi-settings-app',
    Optimizely = 'telia-lt-optimizely',
    EshopComparison = 'telia-lt-eshop-comparison',
    AddressSearchNotFound = 'telia-lt-address-search-not-found',
    ModemOrdering = 'telia-lt-modem-ordering',
    PdfViewer = 'telia-lt-pdf-viewer',
    TVOrdering = 'telia-lt-tv-ordering',
    Plans = 'telia-lt-plans',
    InvoiceDeliverySettings = 'telia-lt-invoice-delivery-settings',
    EshopCategory = 'telia-lt-eshop-category',
    EshopProduct = 'telia-lt-eshop-product',
    GTMTracking = 'telia-lt-gtm-tracking',
    EshopPurchaseCustomizationPlan = 'telia-lt-eshop-purchase-customization-plan',
    EshopPurchaseCustomizationLogin = 'telia-lt-eshop-purchase-customization-login',
    CoverageMap = 'telia-lt-coverage-map',
}
